import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/capacitor';
import { StorageService } from './storage.service';
import { BarcodeScannerService } from './barcode-scanner.service';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AuthService } from './auth.service';
import { NetworkService } from './network.service';
import { LoadingService } from './loading.service';
import { RouteRepository, UserRepository } from '@app/repositories';
import { environment } from '@env/environment';
import { SqliteService } from './sqlite.service';
import { AlertService } from './alert.service';
import { WebSocketService } from './web-socket.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { App } from '@capacitor/app';
import { AlertController } from '@ionic/angular/standalone';
import { Keyboard } from '@capacitor/keyboard';

@Injectable()
export class InitializeAppService
{
    private alertController = inject(AlertController);
    private loadingService = inject(LoadingService);
    private networkService = inject(NetworkService);
    private authService = inject(AuthService);
    private storageService = inject(StorageService);
    private sqliteService = inject(SqliteService);
    private barcodeScannerService = inject(BarcodeScannerService);
    private userRepository = inject(UserRepository);
    private alertService = inject(AlertService);
    private routeRepository = inject(RouteRepository);
    private router = inject(Router);
    private webSocketService = inject(WebSocketService);

    async initializeApp()
    {
        try
        {
            await SplashScreen.hide({ fadeOutDuration: 500, });

            await this.loadingService.show('Initializing, please wait...');

            await this.networkService.initialize();

            await this.initializeStorage();
            await this.initializeBarcode();
            await this.initializeAuthStatus();

            await this.checkIfOnRouteAndRedirect();

            if (Capacitor.isNativePlatform())
            {
                // this.initializeInAppBrowser();

                if (Capacitor.getPlatform() === 'android')
                {
                    await EdgeToEdge.setBackgroundColor({ color: '#000000', });
                    await EdgeToEdge.enable();
                }

                await StatusBar.setStyle({ style: Style.Dark, });
                await StatusBar.setBackgroundColor({ color: '#000000', });
                await StatusBar.show();

                if (Capacitor.getPlatform() === 'ios')
                {
                    await Keyboard.setAccessoryBarVisible({ isVisible: true, });
                }

                // await this.initializeLiveUpdate();
            }
        }
        catch (e: any)
        {
            console.error('initializeApp', e);
        }
        finally
        {
            await this.loadingService.hide();
            await SplashScreen.hide({ fadeOutDuration: 500, });
        }
    }

    private async initializeLiveUpdate()
    {
        try
        {
            await this.checkForLiveUpdates();

            await App.addListener('resume', async () =>
            {
                console.info('App.resume');

                await this.checkForLiveUpdates();
            });
        }
        catch (error: any)
        {
            console.error('initializeLiveUpdate', error);
        }
    }

    private async checkForLiveUpdates()
    {
        try
        {
            console.info('checkForLiveUpdates');

            const isOnRoute = await this.checkIfOnRouteAndRedirect(false);

            if (isOnRoute)
            {
                console.info('skip live update check because isOnRoute:', isOnRoute);

                return;
            }

            const isOnline = this.networkService.isOnline();

            if (!isOnline)
            {
                console.info('skip live check because isOnline:', isOnline);

                return;
            }

            await this.loadingService.show('Checking for an updates...');

            const { nextBundleId } = await LiveUpdate.sync();

            console.info('await LiveUpdate.sync', nextBundleId);

            if (nextBundleId)
            {
                console.info('checkForLiveUpdates', 'perform reload');

                await LiveUpdate.reload();
            }
        }
        catch (e: any)
        {
            console.error('checkForLiveUpdates', e);
        }
        finally
        {
            await this.loadingService.hide();
        }
    }

    private initializeInAppBrowser()
    {
        document.body.addEventListener('click', async (event) =>
        {
            console.info('document.body.click');

            const target = event.target as HTMLElement;
            const link = target.closest('a[href]');

            if (!link)
            {
                return;
            }

            const href = link.getAttribute('href');

            if (!href)
            {
                return;
            }

            event.preventDefault();

            await Browser.open({ url: href, });
        }, true);
    }

    private async initializeStorage()
    {
        await this.storageService.initialize();

        try
        {
            await this.sqliteService.initialize();
        }
        catch (error: any)
        {
            Sentry.captureException(error);
            const message = error.message ? error.message : error;

            const alert = await this.alertController.create({
                header: 'Storage Initialization Fail',
                subHeader: 'Please completely force close the app and open again.',
                message: message,
                animated: true,
                backdropDismiss: false,
                keyboardClose: true,
                buttons: [
                    {
                        text: 'OK',
                        role: 'cancel',
                        handler: () =>
                        {
                            if (Capacitor.isNativePlatform())
                            {
                                App.exitApp();
                            }
                            else
                            {
                                window.location.reload();
                            }
                        }
                    },
                ],
            });
            await alert.present();

            await this.loadingService.hide();
            await SplashScreen.hide({ fadeOutDuration: 500, });

            throw error;

            // await this.alertService.show('An error occurred during sqlite service initialization: ' + message, 6000);
        }
    }

    private async initializeBarcode()
    {
        await this.barcodeScannerService.initialize();
    }

    private async initializeAuthStatus()
    {
        const status = await this.checkIfUserAndTokenAreSaved();

        Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Checked saved auth state',
            level: 'info',
            data: {
                hasSavedAuth: status,
            },
        });

        // means that we have a token and a user,
        // and now we need to check if the token is still valid
        if (status)
        {
            const tokenIsValid = await this.checkIfSavedTokenIsValid();

            if (tokenIsValid)
            {
                Sentry.addBreadcrumb({
                    category: 'auth',
                    message: 'Saved auth state accepted',
                    level: 'info',
                });

                await this.webSocketService.listenForForceLogoutEvents();
            }
            else
            {
                Sentry.addBreadcrumb({
                    category: 'auth',
                    message: 'Saved auth state rejected, clearing local auth data',
                    level: 'warning',
                });

                this.webSocketService.unsubscribeFromAll();

                await this.sqliteService.deleteDatabase();
                await this.userRepository.logout();
            }
        }
    }

    private async checkIfUserAndTokenAreSaved(): Promise<boolean>
    {
        try
        {
            const token = await this.userRepository.getAuthToken();

            // no token at all means no user is logged in.
            // let's clear everything and start fresh
            if (!token)
            {
                Sentry.addBreadcrumb({
                    category: 'auth',
                    message: 'No saved auth token found',
                    level: 'info',
                });

                this.authService.isLoggedIn.set(false);
                await this.userRepository.logout();

                return false;
            }

            const user = await this.userRepository.getLoggedInUser();

            // this does not happen normally, but just in case,
            // if the token is there and the user is not, assume the user is not logged in.
            // let's clear everything and start fresh.
            // this way we can be sure later in the app that the method above returns UserModel
            // and not NULL after the user is logged in
            if (!user)
            {
                Sentry.addBreadcrumb({
                    category: 'auth',
                    message: 'Saved auth token exists but saved user is missing',
                    level: 'warning',
                });

                this.authService.isLoggedIn.set(false);
                await this.userRepository.logout();

                return false;
            }

            return true;
        }
        catch (error: any)
        {
            console.error('checkIfUserAndTokenAreSaved', error);
            Sentry.captureException(error);

            this.authService.isLoggedIn.set(false);
            await this.userRepository.logout();

            return false;
        }
    }

    private async checkIfSavedTokenIsValid(): Promise<boolean>
    {
        // If a device is offline
        // we assume that this user is logged in fine
        // and is on its route without internet connection.
        // so we do not clear anything and let the user continue its work
        if (!this.networkService.isOnline())
        {
            Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Skipped token verification because device is offline',
                level: 'info',
            });

            this.authService.isLoggedIn.set(true);

            return true;
        }

        // for only testing purposes
        if (!environment.production && !environment.verifyAuth)
        {
            Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Skipped token verification because verifyAuth is disabled',
                level: 'info',
            });

            this.authService.isLoggedIn.set(true);

            return true;
        }

        let isLoggedIn = false;

        try
        {
            isLoggedIn = await this.authService.verify();
        }
        catch (error: any)
        {
            console.error('checkIfSavedTokenIsValid verify', error);

            // we will log out user here only if we have explicit 401 status as a backend response
            // we do not want to log out user if we have any other error
            // like connectivity issues
            // or DNS or backend server issues
            if (error instanceof HttpErrorResponse && error.status === 401)
            {
                Sentry.captureMessage('Auth token verification failed with 401', {
                    level: 'warning',
                    extra: {
                        status: error.status,
                        url: error.url,
                        error: error.error,
                    },
                });

                this.authService.isLoggedIn.set(false);
                await this.userRepository.logout();

                await this.alertService.show('You were logged out because token verification failed and status was 401.', 8000);

                return false;
            }

            // so if we have not a backend problem
            // then will assume that user has
            // bad internet connection and is logged in
            Sentry.captureMessage('Auth token verification request failed, keeping saved auth state', {
                level: 'warning',
                extra: {
                    status: error instanceof HttpErrorResponse ? error.status : null,
                    url: error instanceof HttpErrorResponse ? error.url : null,
                    message: error?.message,
                    error: error instanceof HttpErrorResponse ? error.error : error,
                },
            });

            this.authService.isLoggedIn.set(true);

            return true;
        }

        // we do not set here true by default
        // as the verify method returns false if the token is expired
        // or not valid at all
        // so we set the status from the backend
        this.authService.isLoggedIn.set(isLoggedIn);

        if (isLoggedIn)
        {
            const user = await this.authService.me();
            await this.userRepository.setLoggedInUser(user);
        }
        else
        {
            Sentry.captureMessage('Auth token verification returned false', {
                level: 'warning',
            });

            await this.userRepository.logout();
            await this.alertService.show('You were logged out because token verification failed.', 8000);
        }

        return isLoggedIn;
    }

    private async checkIfOnRouteAndRedirect(needToRedirect = true): Promise<boolean>
    {
        let isOnRoute = false;
        const activeRoute = await this.routeRepository.getActiveRoute();

        if (activeRoute !== null)
        {
            isOnRoute = true;
            await this.webSocketService.listenForRouteEvents(activeRoute);

            if (needToRedirect)
            {
                await this.router.navigate(['tabs', 'active-route', activeRoute.id], { replaceUrl: true, });
            }
        }

        return isOnRoute;
    }
}
